import React, { useState, useEffect } from "react";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { Card, Autocomplete, TextField, Snackbar, Alert, createFilterOptions } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  setRouteReplayData,
  setHaltData,
  setIgnitionData,
  setRFIDData,
  clearRouteReplayData,
  clearRouteCurrentPacket,
  clearJourneyPlotData,
  clearSeletectedJourneyPlotData,
} from "features/routeReplay/routeReplaySlice";
// import { fetchJourneyData } from "features/routeReplay/routeReplayThunk";
import { fetchThingDetails } from "features/thing/thingThunk";
import { routeReplayReport, haltReport, ignition } from "config/apis";
import { setRouteReplayVehicle, setAutoLoadRoute, setStoredTimes, resetRouteReplayVehicle } from "features/vehicle/vehicleSlice";
import DateRangePicker from "layouts/DateTimerange/dateTimeRange";

const FilterCard = ({ onClearTable }) => {
  const [vehicle, setVehicle] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  // Snackbar State for individual API error tracking
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const dispatch = useDispatch();
  const things = useSelector((state) => state.things.filterThing || []);
  const isVehicle = useSelector((state) => state.vehicle.routeReplayVehicleId) || "";
  const autoLoad = useSelector((state) => state.vehicle.autoLoadRoute);

  const formatDateTime = (dateTime) => {
    if (!dateTime) return "";
    return dateTime.replace(/-/g, "/").replace("T", " ");
  };

  const formatForInput = (date) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const vehicleFilterOptions = createFilterOptions({
    stringify: (option) =>
      `${option?.name || ""} ${option?.nickName || ""} ${option?.vehicleNumber || ""} ${option?.id || ""}`,
  });

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    dispatch(fetchThingDetails());
  }, [dispatch]);

  useEffect(() => {
    if (!things.length || !isVehicle) return;
    const thing = things.find((e) => e.id === isVehicle);
    if (!thing) return;
    setVehicle(thing.id);

    setStartTime((prevStart) => {
      if (prevStart) return prevStart;
      const now = new Date();
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      return formatForInput(start);
    });
    setEndTime((prevEnd) => {
      if (prevEnd) return prevEnd;
      const now = new Date();
      const end = new Date(now);
      end.setHours(23, 59, 0, 0);
      return formatForInput(end);
    });
  }, [things, isVehicle]);

  useEffect(() => {
    if (autoLoad && vehicle && startTime && endTime) {
      handleLoad();
      dispatch(setAutoLoadRoute(false));
    }
  }, [autoLoad, vehicle, startTime, endTime, dispatch]);

  const handleClear = () => {
    setVehicle(""); setStartTime(""); setEndTime(""); setError(null);
    dispatch(resetRouteReplayVehicle());
    dispatch(clearRouteReplayData());
    dispatch(clearRouteCurrentPacket());
    dispatch(clearSeletectedJourneyPlotData());
    dispatch(clearJourneyPlotData());

    // Table (SpeedAnalysis) ko close/clear karne ke liye
    if (onClearTable) {
      onClearTable();
    }
  };

  // Helper function to handle individual API calls and trigger snackbar on failure
  const fetchAndDispatch = async (url, body, action, label, headers) => {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      const data = await response.json();

      if (response.ok) {
        dispatch(action(data));
        console.log(`${label} data loaded successfully. Response:`, data);
        return data;
      }
      setSnackbar({
        open: true,
        message: `${label}: ${data.message || "Failed to fetch data"}`,
        severity: "error",
      });
      return null;

    } catch (err) {
      setSnackbar({
        open: true,
        message: `${label}: Connection error`,
        severity: "warning",
      });
    }
  };

  const isRouteReplayDataEmpty = (data) => {
  if (!data) return true;
  const hasDevices = data.devices && Object.keys(data.devices).length > 0;
  const hasMovement =
    Number(data.distanceTravelled) > 0 ||
    Number(data.activeDuration) > 0 ||
    Number(data.runningTime) > 0;
  return !hasDevices && !hasMovement;
};

  const handleLoad = async () => {
    if (!vehicle || !startTime || !endTime) {
      setError("Please fill in all fields.");
      return;
    }
    setLoading(true);
    setError(null);

    const token = localStorage.getItem("token");
    const apiStart = formatDateTime(startTime);
    const apiEnd = formatDateTime(endTime);

    dispatch(setStoredTimes({ start: startTime, end: endTime }));

    const headers = {
      "x-dhundhoo-session": token,
      "Content-Type": "application/json",
    };

    // Clear previous data
    dispatch(clearRouteReplayData());
    dispatch(clearRouteCurrentPacket());
    dispatch(clearSeletectedJourneyPlotData());
    dispatch(clearJourneyPlotData());

    // Fetch Route Replay data
   const routeReplayData = await fetchAndDispatch(
  `${routeReplayReport}/${vehicle}`,
  { timeZone: "Asia/Kolkata", startTime: apiStart, endTime: apiEnd },
  setRouteReplayData,
  "Route Replay",
  headers
);

if (routeReplayData !== null && isRouteReplayDataEmpty(routeReplayData)) {
  // setError("No route data found for the selected vehicle and time range.");
  setSnackbar({
    open: true,
    message: "No route data found for the selected vehicle and time range.",
    severity: "warning",
  });
}

    // Fetch Journey list too, so "Select and plot Journey" dropdown gets populated
    // dispatch(fetchJourneyData(vehicle));

    setLoading(false);
  };

  return (
    <MDBox p={2}>
      <Card sx={{ boxShadow: "none" }}>
        {/* <Autocomplete
          options={things}
          getOptionLabel={(option) =>
            `${option?.name || ""} ${option?.nickName ? `- ${option.nickName}` : ""}`
          }
          value={things.find((v) => v.id === vehicle) || null}
          onChange={(event, newValue) => {
            const id = newValue ? newValue.id : "";
            setVehicle(id);
            dispatch(setIsVehicle(id));
          }}
          renderOption={(props, option) => (
            <li
              {...props}
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span>
                {option.nickName}
              </span>
              <span>{option.name}</span>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search Vehicle / Route"
              fullWidth
              size="small"
              disabled={loading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#FB8C00",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#FB8C00",
                },
              }}
            />
          )}
          sx={{ mb: 2 }}
        /> */}

        <Autocomplete
          options={things}
          filterOptions={vehicleFilterOptions}
          getOptionLabel={(option) =>
            `${option?.thingId || ""} ${option?.thingId ? `- ${option.nickName}` : ""}`
          }
          value={things.find((v) => v.id === vehicle) || null}
          onChange={(event, newValue) => {
            const id = newValue ? newValue.id : "";
            setVehicle(id);
            dispatch(setRouteReplayVehicle(id));
          }}
          renderOption={(props, option) => (
            <li
              {...props}
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span>{option.thingId}</span>
              <span>
                {option.nickName}
                {option.vehicleNumber ? ` (${option.vehicleNumber})` : ""}
              </span>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select the vehicle"
              fullWidth
              size="small"
              disabled={loading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#FB8C00",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#FB8C00",
                },
              }}
            />
          )}
          sx={{ mb: 2 }}
        />

        <MDBox display="flex" alignItems="center" gap={1} mb={2}>
          <DateRangePicker
            startTime={startTime}
            endTime={endTime}
            onChange={({ startTime: s, endTime: e }) => { setStartTime(s); setEndTime(e); }}
            disabled={loading}
          />
        </MDBox>

        {error && <MDBox sx={{ color: "red", fontSize: "0.875rem", mb: 2 }}>{error}</MDBox>}

        <MDBox display="flex" gap={1} mt={2}>
          <MDButton variant="contained" color="info" fullWidth onClick={handleLoad} disabled={loading} sx={{ backgroundColor: "#fb8c00", "&:hover": { backgroundColor: "#fb8c00" } }}>
            {loading ? "Loading..." : "Load"}
          </MDButton>
          <MDButton variant="outlined" color="secondary" fullWidth onClick={handleClear} disabled={loading}>Clear</MDButton>
        </MDBox>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MDBox>
  );
};

export default FilterCard;